import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ClockIcon } from "@phosphor-icons/react";
import CustomText from "../../../common/general/CustomText/CustomText";
import {
  colors,
  radii,
  spacing,
  shadowStyles,
  typographyScale,
  webTransitions,
} from "../../../../styles/design-system";
import { CircleCheckBig } from "lucide-react";

export interface ActivityItem {
  id: string;
  title: string;
  type: string;
  status: "Complete" | "In Progress";
  timeCompleted?: string;
}

interface RecentActivityListProps {
  activities: ActivityItem[];
}

const RecentActivityList: React.FC<RecentActivityListProps> = ({ activities }) => {
  const [hoveredActivityId, setHoveredActivityId] = useState<string | null>(null);

  const getStatusIcon = (status: string) => {
    if (status === "Complete") {
      return <CircleCheckBig size={20} color="#3b7c0f" />;
    }
    return <ClockIcon size={20} color="#075985" weight="regular" />;
  };

  const getStatusColor = (status: string): string => {
    return status === "Complete" ? "#66c61c" : "#F59E0B";
  };

  const getStatusBgColor = (status: string): string => {
    return status === "Complete" ? "#f3fee7" : "#fffaeb";
  };

  const getStatusTextColor = (status: string): string => {
    return status === "Complete" ? "#3b7c0f" : "#b54708";
  };

  return (
    <View style={[styles.container, shadowStyles.sm]}>
      <View style={styles.header}>
        <CustomText text="Recent Activity" size="xl" weight="bold" useTranslationText={false} style={styles.title} />
      </View>

      <View style={styles.listContainer}>
        {activities.length > 0 ? (
          activities.map((activity) => (
            <Pressable
              key={activity.id}
              style={[styles.activityItem, hoveredActivityId === activity.id && styles.activityItemHovered]}
              onHoverIn={() => setHoveredActivityId(activity.id)}
              onHoverOut={() => setHoveredActivityId(null)}>
              <View style={styles.leftContent}>
                {getStatusIcon(activity.status)}
                <View style={styles.typeBadge}>
                  <CustomText
                    text={activity.type}
                    size="xs"
                    weight="medium"
                    useTranslationText={false}
                    style={styles.typeText}
                  />
                </View>
                <CustomText
                  text={activity.title}
                  size="m"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.activityTitle}
                  numberOfLines={1}
                />
              </View>

              <View style={styles.rightContent}>
                {activity.timeCompleted && (
                  <CustomText
                    text={activity.timeCompleted}
                    size="xs"
                    useTranslationText={false}
                    style={styles.timeText}
                  />
                )}
                <View
                  style={[
                    styles.statusBadge,
                    {
                      borderColor: getStatusColor(activity.status),
                      backgroundColor: getStatusBgColor(activity.status),
                    },
                  ]}>
                  <CustomText
                    text={activity.status}
                    size="xs"
                    useTranslationText={false}
                    style={[styles.statusText, { color: getStatusTextColor(activity.status) }]}
                  />
                </View>
              </View>
            </Pressable>
          ))
        ) : (
          <View style={styles.emptyState}>
            <CustomText text="No recent activity" size="s" useTranslationText={false} style={styles.emptyText} />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
  },

  header: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    fontSize: typographyScale.textXs.fontSize,
  },

  title: {
    color: colors.grey[900],
  },

  listContainer: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xs,
  },

  activityItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    marginVertical: spacing.xs,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: "transparent",
    cursor: "auto",
    fontSize: typographyScale.textSm.fontSize,
    ...webTransitions.fast,
  },
  activityItemHovered: {
    backgroundColor: colors.grey[100],
  },

  leftContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    flex: 1,
    paddingRight: spacing.xs,
    fontSize: typographyScale.textSm.fontSize,
  },

  rightContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.lg,
    fontSize: typographyScale.textSm.fontSize,
  },

  typeBadge: {
    backgroundColor: colors.grey[100],
    paddingHorizontal: spacing.xs,
    paddingVertical: 4,
    borderRadius: radii.sm,
    fontSize: typographyScale.textXs.fontSize,
  },
  typeText: {
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: colors.grey[600],
  },
  statusBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radii.full,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    minWidth: 90,
  },
  statusText: {
    fontWeight: "500",
    fontSize: typographyScale.textXs.fontSize,
  },
  activityTitle: {
    color: colors.grey[900],
    flexShrink: 1,
  },
  timeText: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
  },
  emptyState: {
    paddingVertical: spacing.xl,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    color: colors.grey[400],
  },
});

export default RecentActivityList;
